import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { sharePointService } from '../../services/sharePointService';
import authApi from '../../services/authApi';
import { useUserSession } from '../../contexts/UserContext';
import { useSharePoint } from '../../contexts/SharePointContext';
import { useFetchGeneration } from '../../hooks/useFetchGeneration';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';

const ETLStats = () => {
  const theme = useTheme();
  const { userSession } = useUserSession();
  const { effectiveConfigId, refreshTrigger } = useSharePoint();
  const { begin, isCurrent } = useFetchGeneration();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const libraries = stats?.libraries || [];
  const librariesWithItems = libraries.filter(lib => lib.lastItemsUpdate != null);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchETLStats = async () => {
      if (!userSession?.email || !effectiveConfigId) {
        setError('User email not available');
        setLoading(false);
        return;
      }

      const fetchGen = begin();
      try {
        setLoading(true);
        setError(null);
        const token = authApi.getToken();
        const response = await sharePointService.getETLStats(token, effectiveConfigId);
        if (isCurrent(fetchGen)) {
          setStats(response);
        }
      } catch (err) {
        if (isCurrent(fetchGen)) {
          setError(err.message || 'Failed to fetch ETL statistics');
        }
      } finally {
        if (isCurrent(fetchGen)) {
          setLoading(false);
        }
      }
    };

    fetchETLStats();
  }, [userSession?.email, effectiveConfigId, refreshTrigger, begin, isCurrent]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }) + ' EST';
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading ETL statistics: {error}
        </Alert>
      );
    }

    if (!stats?.libraries || librariesWithItems.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No ETL sync data found. This could mean no libraries have been synced yet or the ETL process hasn't started.
        </Alert>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table aria-label="etl sync stats table" size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f4511e' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Library Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Default Permissions Sync</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Items Update</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Permissions Update</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Last Analytics Update</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {librariesWithItems.map((library) => (
              <TableRow key={library.id}>
                <TableCell>{library.name}</TableCell>
                <TableCell>{formatTimestamp(library.lastDefaultPermissionsSync)}</TableCell>
                <TableCell>{formatTimestamp(library.lastItemsUpdate)}</TableCell>
                <TableCell>{formatTimestamp(library.lastPermissionsUpdate)}</TableCell>
                <TableCell>{formatTimestamp(library.lastAnalyticsUpdate)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={'etl-stats'}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          ETL Sync Statistics
        </Typography>
        {renderContent()}
      </Box>
    </Box>
  );
};

export default ETLStats; 